/**
 * Κοινά βοηθήματα οθόνης για τον επεξεργαστή καταλόγου πλατφόρμας (§13.5, §13.11).
 *
 * ΓΙΑΤΙ ξεχωριστό αρχείο: το πλέγμα, οι μαζικές ενέργειες και το καλάθι πρέπει να
 * συμφωνούν ΑΚΡΙΒΩΣ στο τι είναι «εγγράψιμο κελί», ποια είναι η τρέχουσα τιμή και
 * πόσα δίδυμα παρασύρει μια αλλαγή. Αν η κάθε οθόνη το υπολόγιζε μόνη της, η
 * προ-μέτρηση των μαζικών ενεργειών («θα φτιάξει 14 αλλαγές και θα παρασύρει 3
 * δίδυμα») θα έλεγε άλλα από αυτά που τελικά στέλνονται.
 */

import {
  CellState, EditField, EditRow, GridCell, GridCellV5, GridRow, GridStore, OverrideMode,
  PendingEdit, PriceDiffRow, PriceReason, RunProgress,
} from '../../services/platformCatalogApi';

/** Ύψος γραμμής πλέγματος σε px — ΣΤΑΘΕΡΟ, το χρειάζεται η virtualization. */
export const ROW_H = 44;
/** Ύψος κεφαλίδας πλέγματος (δύο σειρές κειμένου στις στήλες καταστημάτων). */
export const HEADER_H = 58;
/** Πλάτος στήλης καταστήματος (§13.11: ~76px). */
export const STORE_COL_W = 76;
/** Πλάτη των παγωμένων αριστερών στηλών. */
export const COL_SELECT_W = 34;
export const COL_NAME_W = 246;
export const COL_PRICE_ALL_W = 118;
/**
 * «Τιμή καταλόγου (delivery)» — Φ5 (§13.19.Α). Παγωμένη στήλη και όχι κελί ανά
 * κατάστημα: η τιμή του καταλόγου μας είναι απόφαση ΤΗΣ ΑΛΥΣΙΔΑΣ (κλειδί
 * `master_key`), ενώ η απόκλιση ανά κατάστημα ζωγραφίζεται μέσα στο κελί.
 */
export const COL_CATALOG_W = 122;
export const COL_AVAIL_ALL_W = 104;
export const FROZEN_W =
  COL_SELECT_W + COL_NAME_W + COL_PRICE_ALL_W + COL_CATALOG_W + COL_AVAIL_ALL_W;

export const CELL_STATE_LABELS: Record<CellState, string> = {
  normal: 'Κανονικό',
  missing: 'Λείπει από το κατάστημα',
  gone: 'Έφυγε από τον κατάλογο',
  local: 'Μόνο σε ορισμένα καταστήματα',
  structure: 'Διαφορετική δομή',
  twin: 'Δίδυμο τιμής',
  offer: 'Είδος προσφοράς',
  pending: 'Εκκρεμής αλλαγή',
  conflict: 'Σύγκρουση',
};

export const FIELD_LABELS: Record<EditField, string> = {
  price: 'Τιμή',
  is_available: 'Διαθεσιμότητα',
  auto_reset: 'Αυτόματη επαναφορά',
  is_hidden: 'Κρυφό',
  stock_amount: 'Απόθεμα',
  create: 'Δημιουργία',
  delete: 'Διαγραφή από efood',
};

/** Ελληνικό δεκαδικό κόμμα — ο χειριστής πληκτρολογεί «1,70» και το διαβάζει έτσι. */
export const fmtMoney = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  // isFinite αντί για Number.isFinite: το build στοχεύει es5 και δεν γίνεται polyfill.
  return isFinite(n) ? n.toFixed(2).replace('.', ',') : '—';
};

export const fmtMoneyEuro = (value: number | string | null | undefined): string => {
  const s = fmtMoney(value);
  return s === '—' ? s : `${s} €`;
};

/**
 * Δέχεται «1,70» και «1.70». Γυρίζει null σε ό,τι δεν είναι τιμή ≥ 0 — ο caller
 * ΔΕΝ δημιουργεί αλλαγή με null (μια κενή φόρμα δεν είναι «τιμή μηδέν»).
 */
export const parsePriceInput = (raw: string): number | null => {
  const text = String(raw == null ? '' : raw).trim().replace(',', '.');
  if (text === '') return null;
  const n = Number(text);
  if (!isFinite(n) || n < 0) return null;
  // Οι τιμές του efood είναι NUMERIC(10,2) — στρογγυλοποιούμε ΕΔΩ ώστε η οθόνη
  // να μη δείχνει 1,6999999 και να μη στέλνει αλλαγή που «δεν φαίνεται».
  return Math.round(n * 100) / 100;
};

/** Ηλικία εισαγωγής σε ανθρώπινα ελληνικά. */
export const fmtAge = (minutes: number | null | undefined): string => {
  if (minutes === null || minutes === undefined || !isFinite(Number(minutes))) return 'ποτέ';
  const m = Math.max(0, Math.round(Number(minutes)));
  if (m < 1) return 'μόλις τώρα';
  if (m < 60) return `${m}΄`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ${h === 1 ? 'ώρα' : 'ώρες'}`;
  const d = Math.floor(h / 24);
  return `${d} ${d === 1 ? 'ημέρα' : 'ημέρες'}`;
};

export const fmtDateTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  const d = new Date(value);
  return isFinite(d.getTime())
    ? d.toLocaleString('el-GR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '—';
};

/**
 * Σύντομο όνομα στήλης (η στήλη είναι 76px, δεν χωρά «Κουλουράδες Νέα Ιωνία»).
 * Κρατάμε την ΤΕΛΕΥΤΑΙΑ λέξη γιατί εκεί ζει η διάκριση: όλα τα καταστήματα
 * λέγονται «Κουλουράδες Χ».
 */
export const storeShort = (name: string, fallback?: string | null): string => {
  if (fallback) return String(fallback);
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  const last = parts[parts.length - 1];
  return last.length <= 6 ? last : last.slice(0, 6);
};

/** Το κελί δείχνει υπαρκτό είδος του καταστήματος (άρα μπορεί να πάρει αλλαγή). */
export const cellIsEditable = (cell: GridCell | undefined): boolean =>
  !!cell && cell.state !== 'missing' && cell.state !== 'gone' && !!cell.item_code;

/**
 * Η εκκρεμής αλλαγή ΤΟΥ ΣΥΓΚΕΚΡΙΜΕΝΟΥ πεδίου, αν υπάρχει.
 *
 * ΓΙΑΤΙ δεν αρκεί το `cell.pending`: το backend βάζει εκεί ΜΙΑ αλλαγή (την
 * σύγκρουση ή την πιο πρόσφατη, buildGrid) και δίνει ολόκληρη τη λίστα στο
 * `pending_edits`. Ένα κελί μπορεί κάλλιστα να έχει ΚΑΙ πρόχειρη τιμή ΚΑΙ
 * πρόχειρη διαθεσιμότητα — αν κοιτούσαμε μόνο το `pending`, η μία από τις δύο
 * θα «εξαφανιζόταν» από την οθόνη ενώ θα εξακολουθούσε να στέλνεται.
 */
const pendingFor = (cell: GridCell | undefined, field: EditField): PendingEdit | null => {
  if (!cell) return null;
  const list = cell.pending_edits;
  if (list && list.length > 0) {
    for (let i = 0; i < list.length; i++) {
      if (list[i] && list[i].field === field) return list[i];
    }
    return null;
  }
  return cell.pending && cell.pending.field === field ? cell.pending : null;
};

/** Η τιμή που ΒΛΕΠΕΙ ο χειριστής: η εκκρεμής αν υπάρχει, αλλιώς η αποθηκευμένη. */
export const shownPrice = (cell: GridCell | undefined): number | null => {
  if (!cell) return null;
  const pend = pendingFor(cell, 'price');
  if (pend) {
    const n = Number(pend.to);
    if (isFinite(n)) return n;
  }
  return cell.price === null || cell.price === undefined ? null : Number(cell.price);
};

export const shownAvailable = (cell: GridCell | undefined): boolean | null => {
  if (!cell) return null;
  const pend = pendingFor(cell, 'is_available');
  if (pend) return !!pend.to;
  return cell.is_available === null || cell.is_available === undefined ? null : !!cell.is_available;
};

/**
 * Η τιμή ΤΗΣ ΠΛΑΤΦΟΡΜΑΣ — αυτή που ταξιδεύει ως `old_value` (§13.6.1).
 *
 * ΠΟΤΕ η εκκρεμής: το `old_value` είναι δήλωση «έτσι το είχε το efood όταν το
 * είδα», και το backend το συγκρίνει με την αποθηκευμένη γραμμή. Αν στέλναμε το
 * πρόχειρο, η ΔΕΥΤΕΡΗ διόρθωση του ίδιου κελιού θα γυρνούσε 409 «η τιμή άλλαξε
 * στο efood» — ψεύτικη σύγκρουση που δεν λύνεται ούτε με νέα εισαγωγή.
 */
export const platformPrice = (cell: GridCell | undefined): number | null =>
  (!cell || cell.price === null || cell.price === undefined) ? null : Number(cell.price);

export const platformAvailable = (cell: GridCell | undefined): boolean | null =>
  (!cell || cell.is_available === null || cell.is_available === undefined) ? null : !!cell.is_available;

/**
 * Πόσα ΕΠΙΠΛΕΟΝ είδη παρασύρει μια αλλαγή σε αυτό το κελί (§13.3.3).
 *
 * Τα ΙΔΙΑ πεδία με τον planner (`TWIN_CARRIED_FIELDS` = price + is_available):
 * το «κάν' το μη διαθέσιμο» πρέπει να σβήσει και το κρυφό είδος της προσφοράς,
 * αλλιώς η προσφορά συνεχίζει να πουλά κάτι που τελείωσε. Το auto_reset/is_hidden
 * είναι ρύθμιση ΤΗΣ ΓΡΑΜΜΗΣ και δεν παρασύρεται.
 *
 * Μόνο όταν όλα τα μέλη έχουν ήδη την ίδια τιμή· αλλιώς η προεπιλογή είναι «μη
 * τα αγγίζεις» (η περίπτωση «Κουλούρι πραλίνα νηστίσιμη») — ίδιος κανόνας με το
 * `applyAll` του planner, ώστε η προ-μέτρηση να λέει ό,τι τελικά στέλνεται.
 */
export const TWIN_CARRIED_FIELDS: EditField[] = ['price', 'is_available'];

export const twinCarry = (cell: GridCell | undefined, field: EditField): number => {
  if (!cell || !cell.twin || cell.twin.members <= 1) return 0;
  if (TWIN_CARRIED_FIELDS.indexOf(field) === -1) return 0;
  return cell.twin.prices_agree ? cell.twin.members - 1 : 0;
};

/** Προεπιλογή του `apply_to_twins` για μια αλλαγή τιμής/διαθεσιμότητας. */
export const defaultApplyToTwins = (cell: GridCell | undefined, field: EditField): boolean =>
  twinCarry(cell, field) > 0;

/**
 * Προ-μέτρηση της στήλης «Τιμή (όλα)»: πόσα καταστήματα θα πάρουν γραμμή edit
 * και πόσα δίδυμα θα παρασυρθούν, ΠΡΙΝ εφαρμοστεί (§13.11).
 *
 * Ο κανόνας «ποιο κελί μετράει» πρέπει να είναι ΓΡΑΜΜΗ ΠΡΟΣ ΓΡΑΜΜΗ ο ίδιος με
 * τον `handleSetPriceAll` της καρτέλας — γι' αυτό και τα δύο περνούν από
 * `cellIsEditable`/`shownPrice`/`twinCarry`.
 */
export const priceAllPreview = (
  row: GridRow, stores: GridStore[], price: number | null
): { stores: number; twins: number } => {
  if (price === null) return { stores: 0, twins: 0 };
  let touched = 0;
  let twins = 0;
  for (let i = 0; i < stores.length; i++) {
    const cell = row.cells ? row.cells[String(stores[i].store_id)] : undefined;
    if (!cellIsEditable(cell) || !cell) continue;
    const before = shownPrice(cell);
    if (before !== null && Math.abs(before - price) < 0.0001) continue;
    touched += 1;
    twins += twinCarry(cell, 'price');
  }
  return { stores: touched, twins };
};

/** «1,60 – 1,80 · 3 τιμές» ή σκέτο «1,60» όταν συμφωνούν όλα (§13.5). */
export const priceAllLabel = (row: GridRow): string => {
  const min = row.price_min === null || row.price_min === undefined ? null : Number(row.price_min);
  const max = row.price_max === null || row.price_max === undefined ? null : Number(row.price_max);
  if (min === null || !isFinite(min)) return '—';
  const distinct = Number(row.price_distinct) || 0;
  if (distinct <= 1 || max === null || !isFinite(max) || max === min) return fmtMoney(min);
  return `${fmtMoney(min)} – ${fmtMoney(max)} · ${distinct} τιμές`;
};

/**
 * Εφαρμόζει στις γραμμές του πλέγματος τις αλλαγές που μόλις γύρισε το backend,
 * ΧΩΡΙΣ να ξαναφορτώσει όλο το πλέγμα.
 *
 * ΓΙΑΤΙ: μια μαζική ενέργεια σε 22 καταστήματα γυρίζει δεκάδες γραμμές edit· ένα
 * πλήρες refetch θα άδειαζε την επιλογή του χειριστή και θα τίναζε το scroll στην
 * κορυφή στη μέση της δουλειάς του. Το ταίριασμα γίνεται με `item_code` ώστε να
 * χρωματίζονται και τα ΔΙΔΥΜΑ, που ζουν σε άλλη γραμμή του πλέγματος.
 */
const toPending = (edit: EditRow): PendingEdit => ({
  edit_id: Number(edit.id),
  field: edit.field,
  from: edit.old_value,
  to: edit.new_value,
  status: edit.status,
});

/**
 * Συγχώνευση νέων αλλαγών σε ένα κελί: ΜΙΑ γραμμή ανά πεδίο (έτσι δουλεύει και
 * το `uq_pce_open`), οι υπόλοιπες μένουν άθικτες. Το `pending` κρατά τη μία
 * κατάσταση που ζωγραφίζεται — η σύγκρουση προηγείται, όπως στο buildGrid.
 */
const mergePending = (cell: GridCell, fresh: EditRow[]): { pending: PendingEdit; list: PendingEdit[] } => {
  const list: PendingEdit[] = (cell.pending_edits && cell.pending_edits.length > 0)
    ? cell.pending_edits.slice()
    : (cell.pending ? [cell.pending] : []);
  let last = toPending(fresh[fresh.length - 1]);
  for (let i = 0; i < fresh.length; i++) {
    const p = toPending(fresh[i]);
    let found = -1;
    for (let j = 0; j < list.length; j++) if (list[j].field === p.field) { found = j; break; }
    if (found >= 0) list[found] = p; else list.push(p);
    last = p;
  }
  let conflict: PendingEdit | null = null;
  for (let i = 0; i < list.length; i++) if (list[i].status === 'conflict') { conflict = list[i]; break; }
  return { pending: conflict || last, list };
};

export const applyEditsToRows = (rows: GridRow[], edits: EditRow[]): GridRow[] => {
  if (!edits || edits.length === 0) return rows;
  const byCode: Record<string, EditRow[]> = {};
  for (let i = 0; i < edits.length; i++) {
    const e = edits[i];
    if (!e || !e.item_code) continue;
    const key = `${e.store_id}|${e.item_code}`;
    if (!byCode[key]) byCode[key] = [];
    byCode[key].push(e);
  }
  return rows.map(row => {
    const cells = row.cells || {};
    const storeKeys = Object.keys(cells);
    let touched = false;
    const next: Record<string, GridCell> = {};
    for (let i = 0; i < storeKeys.length; i++) {
      const key = storeKeys[i];
      const cell = cells[key];
      const fresh = cell && cell.item_code ? byCode[`${key}|${cell.item_code}`] : undefined;
      if (!fresh || fresh.length === 0) { next[key] = cell; continue; }
      touched = true;
      const merged = mergePending(cell, fresh);
      next[key] = { ...cell, pending: merged.pending, pending_edits: merged.list };
    }
    return touched ? { ...row, cells: next } : row;
  });
};

/** Σβήνει κάθε εκκρεμή ένδειξη (μετά από «Καθαρισμός αλλαγών»). */
export const clearPendingInRows = (rows: GridRow[]): GridRow[] =>
  rows.map(row => {
    const cells = row.cells || {};
    const keys = Object.keys(cells);
    let touched = false;
    const next: Record<string, GridCell> = {};
    for (let i = 0; i < keys.length; i++) {
      const cell = cells[keys[i]];
      if (cell && (cell.pending || (cell.pending_edits && cell.pending_edits.length > 0))) {
        touched = true;
        const copy = { ...cell };
        delete copy.pending;
        delete copy.pending_edits;
        next[keys[i]] = copy;
      } else {
        next[keys[i]] = cell;
      }
    }
    return touched ? { ...row, cells: next } : row;
  });

/** Ανθρώπινη τιμή για το καλάθι/ιστορικό: «1,60» ή «Διαθέσιμο». */
export const fmtEditValue = (field: EditField, value: unknown): string => {
  if (field === 'price') return fmtMoney(value as any);
  if (field === 'is_available') return value ? 'Διαθέσιμο' : 'Μη διαθέσιμο';
  if (field === 'auto_reset') return value ? 'Ναι' : 'Όχι';
  if (field === 'is_hidden') return value ? 'Κρυφό' : 'Ορατό';
  if (field === 'delete') return value ? 'Να μην υπάρχει στο efood' : 'Να υπάρχει';
  if (value === null || value === undefined) return '—';
  return String(value);
};

/**
 * Ανθρώπινη τιμή για τις αλλαγές μιας ΚΛΗΣΗΣ του σχεδίου/ιστορικού — ΟΧΙ του καλαθιού.
 *
 * ΓΙΑΤΙ ΞΕΧΩΡΙΣΤΗ ΣΥΝΑΡΤΗΣΗ: οι δύο πλευρές διαβάζουν το ίδιο `delete` με ΑΝΤΙΘΕΤΗ
 * σημασιολογία. Στο καλάθι το `true` είναι ΕΠΙΘΥΜΙΑ («να μην υπάρχει στο efood»).
 * Στο σχέδιο όμως ο planner γράφει ΠΑΡΟΥΣΙΑ: `{field:'delete', from:true, to:false}`
 * = «υπάρχει» → «δεν υπάρχει» (platformCatalogPlanner.js:557-566). Περνώντας τα από το
 * `fmtEditValue`, η στεγνή δοκιμή τύπωνε τη διαγραφή ΑΝΑΠΟΔΑ — με το «Να υπάρχει»
 * σε <strong> ως ΤΕΛΙΚΗ κατάσταση, δηλαδή το σχέδιο που υπογράφει ο χειριστής έλεγε
 * το ΑΝΤΙΘΕΤΟ από ό,τι θα σταλεί. Η σημασιολογία του καλαθιού μένει άθικτη.
 */
export const fmtCallValue = (field: EditField, value: unknown): string => {
  if (field === 'delete') return value ? 'υπάρχει στο efood' : 'διαγράφεται';
  return fmtEditValue(field, value);
};

// ───────────────── Φ5: τιμές καταλόγου, αποφάσεις, διαγραφή ─────────────────

export const OVERRIDE_MODE_LABELS: Record<OverrideMode, string> = {
  catalog: 'Ακολουθεί τον κατάλογο',
  keep_platform: 'Κράτα την τιμή efood',
  fixed: 'Σταθερή τιμή…',
};

export const OVERRIDE_MODE_HINTS: Record<OverrideMode, string> = {
  catalog: 'Προεπιλογή: στέλνεται η τιμή του καναλιού delivery, αυτούσια (χωρίς τέλος πλαστικού).',
  keep_platform: 'Καμία αποστολή δεν αγγίζει ΠΟΤΕ αυτή την τιμή — ούτε τώρα ούτε σε επόμενο συγχρονισμό.',
  fixed: 'Ούτε η τιμή του efood ούτε του καταλόγου: αυτή που γράφεις εδώ.',
};

export const OVERRIDE_MODE_CLASSES: Record<OverrideMode, string> = {
  catalog: 'bg-surface-3 text-t3',
  keep_platform: 'bg-sky-500/20 text-sky-700 dark:text-sky-300',
  fixed: 'bg-violet-500/20 text-violet-700 dark:text-violet-300',
};

/**
 * Οι καταστάσεις εκτέλεσης στα ελληνικά (§11.9).
 *
 * Ο πίνακας κρατά τα αγγλικά (`pcpr_status_chk`) — η οθόνη τα μεταφράζει, γιατί
 * «halted» και «cancelled» σημαίνουν εντελώς διαφορετικά πράγματα για το τι
 * έχει ήδη γραφτεί στο efood και ο χειριστής δεν είναι υποχρεωμένος να το ξέρει.
 */
export const RUN_STATUS_LABELS: Record<string, string> = {
  accepted: 'ξεκίνησε',
  pending: 'σε αναμονή',
  running: 'εκτελείται',
  done: 'ολοκληρώθηκε',
  failed: 'απέτυχε',
  halted: 'σταμάτησε',
  cancelled: 'ακυρώθηκε',
  blocked: 'φραγμένη',
  skipped: 'παραλείφθηκε',
};

export const runStatusLabel = (status?: string | null): string =>
  (status ? (RUN_STATUS_LABELS[status] || status) : 'άγνωστη');

export const PRICE_REASON_LABELS: Record<PriceReason, string> = {
  unmapped: 'χωρίς αντιστοίχιση ονόματος',
  not_in_store: 'το προϊόν δεν υπάρχει σε αυτό το κατάστημα',
  target_ignore: 'αγνοημένο/πακέτο στη φάση 1',
  offer_category: 'είδος κατηγορίας προσφοράς — η τιμή δένεται από τα δίδυμα',
  no_size_match: 'κανένα μέγεθος δεν ταίριαξε',
  missing: 'έφυγε από τον κατάλογο του efood',
  zero_price: 'μηδενική τιμή στον κατάλογο — δεν συγκρίνεται και δεν στέλνεται ποτέ',
  override_ignored_sized: 'η «σταθερή τιμή» αγνοείται σε είδος με μεγέθη — ισχύει ο κατάλογος',
};

/** «+0,10» / «−0,20». Ελληνικό μείον (−), ελληνικό κόμμα. */
export const fmtDelta = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (!isFinite(n)) return '—';
  const abs = fmtMoney(Math.abs(n));
  if (Math.abs(n) < 0.005) return '0,00';
  return `${n > 0 ? '+' : '−'}${abs}`;
};

/**
 * Η ισότητα τιμών ΔΕΝ γίνεται ποτέ με `===`: η Postgres γυρίζει NUMERIC ως
 * string και το `Number()` δίνει 1.7000000000000002. Ίδιο κατώφλι με το backend
 * (§11.1): κάτω από μισό λεπτό = ίδια τιμή.
 */
export const PRICE_EPSILON = 0.005;
export const samePrice = (a: number | null | undefined, b: number | null | undefined): boolean => {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return Math.abs(Number(a) - Number(b)) < PRICE_EPSILON;
};

/**
 * Υπάρχει ενεργή ένδειξη «να μην υπάρχει στο efood» για αυτό το κελί;
 *
 * ΔΕΧΕΤΑΙ ΚΑΙ ΤΑ ΔΥΟ: τη συντόμευση `pending_delete` του backend και σκέτη
 * γραμμή `field='delete'` στα `pending_edits` — ένας server που δεν στέλνει
 * ακόμη τη συντόμευση δεν επιτρέπεται να «κρύψει» διαγραφή που θα φύγει.
 */
export const hasPendingDelete = (cell: GridCellV5 | undefined): boolean => {
  if (!cell) return false;
  if (cell.pending_delete === true) return true;
  const list = (cell.pending_edits && cell.pending_edits.length > 0)
    ? cell.pending_edits
    : (cell.pending ? [cell.pending] : []);
  for (let i = 0; i < list.length; i++) {
    const p = list[i];
    if (p && p.field === 'delete' && p.status !== 'cancelled' && !!p.to) return true;
  }
  return false;
};

/** Σύνοψη σύγκρισης για ΕΝΑ κελί (είδος + τα μεγέθη του σε ένα κατάστημα). */
export interface PriceCellInfo {
  /** Πόσες γραμμές σύγκρισης με διαφορά (είδος και/ή μεγέθη). */
  rows: number;
  catalog: number | null;
  platform: number | null;
  /** Η ΜΕΓΑΛΥΤΕΡΗ κατ' απόλυτη τιμή διαφορά του κελιού. */
  delta: number | null;
  mode: OverrideMode;
  wouldSend: number;
}

/** Σύνοψη σύγκρισης για ΕΝΑ κεντρικό είδος, σε όλη την αλυσίδα. */
export interface PriceMasterInfo {
  master_key: string;
  name: string;
  category_label: string;
  /** Σε πόσα καταστήματα εμφανίστηκε διαφορά. */
  stores: number;
  catalog_min: number | null;
  catalog_max: number | null;
  /** Η μεγαλύτερη διαφορά (με πρόσημο) που μετρήθηκε. */
  delta: number | null;
  mode: OverrideMode;
  note: string | null;
  fixed_price: number | null;
  wouldSend: number;
  reason: PriceReason | null;
  /**
   * Έστω μία γραμμή του είδους τιμολογείται μέσω επιλογής μεγέθους. Τότε η
   * απόφαση «σταθερή τιμή» ΔΕΝ προσφέρεται: θα άλλαζε μόνο την τιμή του είδους
   * και θα άφηνε τα μεγέθη στην τιμή της πλατφόρμας (§11.7).
   */
  sized: boolean;
  rows: PriceDiffRow[];
}

export interface PriceIndex {
  byMaster: Record<string, PriceMasterInfo>;
  /** κλειδί = `master_key|store_id` — ίδια σύμβαση με τα κελιά του πλέγματος. */
  byCell: Record<string, PriceCellInfo>;
  /** Φορτώθηκε ΟΛΗ η σύγκριση (και όσα ταιριάζουν), όχι μόνο οι διαφορές. */
  complete: boolean;
  rows: number;
  masters: number;
}

export const EMPTY_PRICE_INDEX: PriceIndex = {
  byMaster: {}, byCell: {}, complete: false, rows: 0, masters: 0,
};

/**
 * Πότε ΕΠΙΤΡΕΠΕΤΑΙ η οθόνη να γράψει «ίδια» για γραμμή που δεν είδε.
 *
 * ΤΡΕΙΣ όροι, όλοι υποχρεωτικοί — καθένας κλείνει τη ΔΙΚΗ του πόρτα στο ίδιο ψέμα:
 *
 *  1. `onlyMismatch=false` — ζητήθηκε ΟΛΗ η σύγκριση, όχι «μόνο οι διαφορές». Οι
 *     γραμμές χωρίς διαφορά (`delta = null` — χωρίς αντιστοίχιση, εκτός καταστήματος,
 *     είδος προσφοράς) είναι ακριβώς αυτές που λείπουν με το φίλτρο ανοιχτό.
 *  2. `truncated=false` — ο server ΔΕΝ έκοψε τη λίστα στο δικό της ταβάνι (6000). Οι
 *     γραμμές χωρίς διαφορά μπαίνουν ΤΕΛΕΥΤΑΙΕΣ στην ταξινόμηση κατά |διαφορά|, άρα
 *     κόβονται πρώτες.
 *  3. `storeScoped=false` — η σύγκριση αφορά ΟΛΟΝ τον οργανισμό. Το πλέγμα δείχνει
 *     ΟΛΑ τα καταστήματα και το tooltip του λέει κυριολεκτικά «καμία διαφορά … σε
 *     κανένα κατάστημα»: με σύγκριση ΕΝΟΣ καταστήματος αυτό είναι ισχυρισμός για
 *     τα άλλα 19 που δεν ρωτήθηκαν ποτέ.
 *
 * Όλοι και οι τρεις είναι ονόματα του ΙΔΙΟΥ ψέματος: θετικός ισχυρισμός συμφωνίας για
 * γραμμές που δεν παρελήφθησαν.
 */
export const priceIndexComplete = (
  onlyMismatch: boolean, truncated: boolean, storeScoped: boolean
): boolean => !onlyMismatch && !truncated && !storeScoped;

/**
 * «Τρέχει» ≠ «δεν ξέρω τι γίνεται» (§11.9).
 *
 * Η δημοσκόπηση δεν τερματίζει ποτέ μόνη της σε σφάλμα μεταφοράς — σωστά, γιατί η
 * εκτέλεση ΣΥΝΕΧΙΖΕΙ στον server. Αλλά τα παράθυρα που κλειδώνουν το κλείσιμο
 * «όσο τρέχει» δεν επιτρέπεται να κλειδώνουν και όσο «δεν ξέρουν»: κομμένο δίκτυο ή
 * νεκρή διεργασία server θα έκλεινε τον χειριστή ΜΕΣΑ στο modal μέχρι το F5 — με το
 * ίδιο το modal να του λέει «δες το Ιστορικό».
 */
export const runWatchStuck = (progress: RunProgress | null | undefined): boolean =>
  !!(progress && (progress.unreachable
    || (progress.detail && progress.detail.interrupted === true)));

/** Η αυστηρότερη απόφαση κερδίζει όταν ένα κελί έχει πολλές γραμμές. */
const strongerMode = (a: OverrideMode, b: OverrideMode): OverrideMode => {
  if (a === 'keep_platform' || b === 'keep_platform') return 'keep_platform';
  if (a === 'fixed' || b === 'fixed') return 'fixed';
  return 'catalog';
};

const biggerDelta = (a: number | null, b: number | null): number | null => {
  if (a === null) return b;
  if (b === null) return a;
  return Math.abs(b) > Math.abs(a) ? b : a;
};

/**
 * Χτίζει τα δύο ευρετήρια που χρειάζεται η οθόνη από τις γραμμές του
 * `price-diff`.
 *
 * ΓΙΑΤΙ ευρετήριο και όχι φιλτράρισμα στη ζωγραφική: το πλέγμα ζωγραφίζει ~500
 * κελιά σε κάθε κύλιση· ένα `rows.filter()` ανά κελί πάνω σε 4.000 γραμμές
 * σύγκρισης θα έκανε 2.000.000 συγκρίσεις ανά frame.
 */
export const buildPriceIndex = (rows: PriceDiffRow[], complete: boolean): PriceIndex => {
  const byMaster: Record<string, PriceMasterInfo> = {};
  const byCell: Record<string, PriceCellInfo> = {};
  const list = rows || [];

  for (let i = 0; i < list.length; i++) {
    const r = list[i];
    if (!r || !r.master_key) continue;
    const delta = (r.delta === null || r.delta === undefined) ? null : Number(r.delta);
    const catalog = (r.catalog_price === null || r.catalog_price === undefined) ? null : Number(r.catalog_price);
    const platform = (r.platform_price === null || r.platform_price === undefined) ? null : Number(r.platform_price);
    const mode: OverrideMode = r.override_mode || 'catalog';
    const differs = delta !== null && Math.abs(delta) >= PRICE_EPSILON;

    let master = byMaster[r.master_key];
    if (!master) {
      master = {
        master_key: r.master_key,
        name: r.name || r.master_key,
        category_label: r.category_label || '',
        stores: 0,
        catalog_min: null,
        catalog_max: null,
        delta: null,
        mode,
        note: r.override_note || null,
        fixed_price: null,
        wouldSend: 0,
        reason: r.reason || null,
        sized: false,
        rows: [],
      };
      byMaster[r.master_key] = master;
    }
    master.rows.push(r);
    master.mode = strongerMode(master.mode, mode);
    if (!master.note && r.override_note) master.note = r.override_note;
    if (mode === 'fixed' && catalog !== null && master.fixed_price === null) master.fixed_price = catalog;
    if (catalog !== null) {
      master.catalog_min = master.catalog_min === null ? catalog : Math.min(master.catalog_min, catalog);
      master.catalog_max = master.catalog_max === null ? catalog : Math.max(master.catalog_max, catalog);
    }
    if (differs) master.delta = biggerDelta(master.delta, delta);
    if (r.would_send) master.wouldSend += 1;
    if (master.reason === null && r.reason) master.reason = r.reason;
    // Αρκεί ΜΙΑ γραμμή μεγέθους: το είδος τιμολογείται μέσω επιλογών.
    if (r.is_sized === true || r.option_code) master.sized = true;

    const cellKey = `${r.master_key}|${r.store_id}`;
    let cell = byCell[cellKey];
    if (!cell) {
      cell = { rows: 0, catalog: null, platform: null, delta: null, mode, wouldSend: 0 };
      byCell[cellKey] = cell;
      if (differs) master.stores += 1;
    } else if (differs && cell.rows === 0) {
      master.stores += 1;
    }
    cell.mode = strongerMode(cell.mode, mode);
    if (differs) {
      cell.rows += 1;
      cell.delta = biggerDelta(cell.delta, delta);
    }
    // Η τιμή ΤΟΥ ΕΙΔΟΥΣ είναι η γραμμή χωρίς επιλογή· οι γραμμές μεγέθους
    // αφορούν ΑΠΟΛΥΤΕΣ τιμές επιλογών και δεν είναι «η τιμή του κελιού».
    if (!r.option_code) {
      if (catalog !== null) cell.catalog = catalog;
      if (platform !== null) cell.platform = platform;
    } else {
      if (cell.catalog === null && catalog !== null) cell.catalog = catalog;
      if (cell.platform === null && platform !== null) cell.platform = platform;
    }
    if (r.would_send) cell.wouldSend += 1;
  }

  const keys = Object.keys(byMaster);
  return { byMaster, byCell, complete, rows: list.length, masters: keys.length };
};

/** Ταξινόμηση εξ ορισμού (§11.1): |διαφορά| φθίνουσα, μετά όνομα. */
export const sortMastersByDelta = (masters: PriceMasterInfo[]): PriceMasterInfo[] => {
  const out = masters.slice();
  out.sort((a, b) => {
    const da = a.delta === null ? -1 : Math.abs(a.delta);
    const db = b.delta === null ? -1 : Math.abs(b.delta);
    if (da !== db) return db - da;
    return String(a.name).localeCompare(String(b.name), 'el');
  });
  return out;
};
